// Formatting utilities
import { DEFAULT_CURRENCY_CODE, type CurrencyCode } from "@erp/shared-types";

const BASIC_NUMBER_FORMATTER = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const CURRENCY_TO_FORMATTER: { [k: string]: Intl.NumberFormat } = {
  USD: new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
};

export const formatCurrency = (
  value: string | number,
  currency = "USD",
): string => {
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "$0.00";

  // Use accounting-style formatting for negative numbers: $(100.00)
  if (numValue < 0) {
    const formatted = BASIC_NUMBER_FORMATTER.format(Math.abs(numValue));
    return `$(${formatted})`;
  }

  let formatter = CURRENCY_TO_FORMATTER[currency];

  if (!formatter) {
    formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    CURRENCY_TO_FORMATTER[currency] = formatter;
  }

  return formatter.format(numValue);
};

export const formatCurrencyAdvanced = (
  amount: number,
  currency: CurrencyCode = DEFAULT_CURRENCY_CODE,
  options: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {},
) => {
  const value = new Intl.NumberFormat("en-US", {
    ...options,
    style: "currency",
    currency,
  }).format(amount);
  return value;
};

export const formatDate = (date: Date | string): string => {
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatDateTime = (date: Date | string): string => {
  const d = new Date(date);
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatPercentage = (value: number | string): string => {
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  return `${numValue}%`;
};

/**
 * Extract the first whitespace-delimited token from a free-form name
 * (e.g. "John Doe" → "John"). Returns the input unchanged if it is empty,
 * whitespace-only, or otherwise has no usable first token, so callers always
 * get a non-degraded greeting.
 */
export function firstName(fullName: string): string {
  const first = fullName.trim().split(/\s+/)[0];
  return first || fullName;
}

/**
 * Get the month name for a 1-based month number.
 */
function monthName(month: number, short: boolean): string {
  return new Date(2000, month - 1).toLocaleDateString("en-US", {
    month: short ? "short" : "long",
  });
}

/**
 * Parse a YYYY-MM string into year and 1-based month.
 */
function parseYearMonth(ym: string): { year: number; month: number } {
  const parts = ym.split("-");
  return { year: Number(parts[0]), month: Number(parts[1]) };
}

/**
 * Check if two YYYY-MM strings are consecutive months.
 */
function isConsecutive(a: string, b: string): boolean {
  const pa = parseYearMonth(a);
  const pb = parseYearMonth(b);
  if (pa.year === pb.year) return pb.month === pa.month + 1;
  return pa.month === 12 && pb.month === 1 && pb.year === pa.year + 1;
}

/**
 * Format a year as a string, substituting "this year" for the current year.
 */
function formatYear(year: number): string {
  return year === new Date().getFullYear() ? "this year" : String(year);
}

/**
 * Format a range of consecutive YYYY-MM values as a human-readable string.
 */
function formatRange(
  range: string[],
  short: boolean,
  includeYear: boolean,
): string {
  const firstStr = range[0];
  if (!firstStr) return "";
  const first = parseYearMonth(firstStr);
  const mn = (m: number) => monthName(m, short);
  const yr = (y: number) => (includeYear ? ` ${formatYear(y)}` : "");

  if (range.length === 1) {
    return `${mn(first.month)}${yr(first.year)}`;
  }
  const lastStr = range[range.length - 1];
  if (!lastStr) return `${mn(first.month)}${yr(first.year)}`;
  const last = parseYearMonth(lastStr);
  if (first.year === last.year) {
    return `${mn(first.month)} - ${mn(last.month)}${yr(first.year)}`;
  }
  return `${mn(first.month)}${yr(first.year)} - ${mn(last.month)}${yr(last.year)}`;
}

/**
 * Convert a camelCase or dot-notation key into a human-readable label.
 *
 * Examples:
 *   "orgCode"        → "Org Code"
 *   "orderType.id"   → "Order Type Id"
 *   "APInvoice"      → "AP Invoice"
 *   "ABBRCamelCase"  → "ABBR Camel Case"
 */
export function prettifyKey(key: string): string {
  return key
    .split(".")
    .map((segment) =>
      segment
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase()),
    )
    .join(" ");
}

export interface FormatMonthRangesOptions {
  /** Use short month names (e.g. "Jan" instead of "January"). Default: false */
  short?: boolean;
  /** Include year suffix (e.g. "2025" or "this year"). Default: true */
  includeYear?: boolean;
}

/**
 * Format an array of YYYY-MM strings into human-readable ranges.
 *
 * The current year is replaced with "this year" for natural phrasing.
 *
 * Examples (assuming current year is 2026):
 * - ["2026-01"] → "January this year"
 * - ["2026-01", "2026-02", "2026-03"] → "January - March this year"
 * - with { short: true, includeYear: false } → "Jan - Mar"
 * - ["2025-07", "2025-08"] → "July - August 2025"
 */
export function formatMonthRanges(
  months: string[],
  options?: FormatMonthRangesOptions,
): string {
  if (months.length === 0) return "";
  const short = options?.short ?? false;
  const includeYear = options?.includeYear ?? true;
  const sorted = [...months].sort();

  const ranges: string[][] = [];
  const first = sorted[0];
  if (!first) return "";
  let current: string[] = [first];

  for (let i = 1; i < sorted.length; i++) {
    const item = sorted[i];
    if (!item) continue;
    const tail = current[current.length - 1];
    if (tail && isConsecutive(tail, item)) {
      current.push(item);
    } else {
      ranges.push(current);
      current = [item];
    }
  }
  ranges.push(current);

  return ranges.map((r) => formatRange(r, short, includeYear)).join(" & ");
}
